package net.dipgo.game.board

import net.dipgo.game.PointState
import net.dipgo.game.PointState.EMPTY

enum class StandardBoardSizes {
    NINE,
    THIRTEEN,
    NINETEEN;

    fun boardSizeAsInt(boardSize: StandardBoardSizes): Int {
        return when (boardSize) {
            NINE -> 9
            THIRTEEN -> 13
            NINETEEN -> 19
        }
    }
}

class BoardPosition(
    val xSize: Int,
    val ySize: Int
) {

    companion object {
        fun xCoordToLetter(xCoordAsNumber: Int): String {
            return when (xCoordAsNumber) {
                1 -> "A"
                2 -> "B"
                3 -> "C"
                4 -> "D"
                5 -> "E"
                6 -> "F"
                7 -> "G"
                8 -> "H"
                9 -> "J"
                10 -> "K"
                11 -> "L"
                12 -> "M"
                13 -> "N"
                14 -> "O"
                15 -> "P"
                16 -> "Q"
                17 -> "R"
                18 -> "S"
                19 -> "T"
                20 -> "U"
                21 -> "V"
                22 -> "W"
                23 -> "X"
                24 -> "Y"
                25 -> "Z"
                else -> "?"
            }
        }
    }

    private val state: Array<Array<PointState>> = Array(xSize) {
        Array(ySize) { EMPTY }
    }

    fun getBoard(): BoardPosition {
        val board = BoardPosition(xSize, ySize)
        for (x in 0 until xSize) {
            for (y in 0 until ySize) {
                board.placeStone(state[x][y], x, y)
            }
        }
        return board
    }

    fun getStateOfBoard(): Array<Array<PointState>> {
        return state
    }

    fun getStateOfPoint(xCoord: Int, yCoord: Int): PointState {
        return state[xCoord][yCoord]
    }

    fun placeStone(colour: PointState, xCoord: Int, yCoord: Int): Boolean {
        if (xCoord < 0 || yCoord < 0 || xCoord >= xSize || yCoord >= ySize) {
            throw IllegalArgumentException("Attempting to play stone outside board parameters")
        }
        if (getStateOfPoint(xCoord, yCoord) != EMPTY) {
            throw IllegalArgumentException(
                "Attempting to play stone ${colour.name} on point $xCoord,$yCoord which is already occupied by " + getStateOfPoint(
                    xCoord,
                    yCoord
                )
            )
        }
        state[xCoord][yCoord] = colour
        return true
    }

    fun removeStone(xCoord: Int, yCoord: Int): Boolean {
        if (xCoord < 0 || yCoord < 0 || xCoord >= xSize || yCoord >= ySize) {
            throw RuntimeException("Attempting to remove stone outside board parameters")
        }
        if (getStateOfPoint(xCoord, yCoord) == EMPTY) {
            return false
        }
        state[xCoord][yCoord] = EMPTY
        return true
    }

    fun numberOfStonesOnBoard(): Int = state.sumOf { it.count { point -> point != EMPTY } }
    fun numberOfStonesOnBoard(colour: PointState): Int =
        state.sumOf { it.count { point -> point == colour } }

    fun removeAllStonesOfColour(pointState: PointState) {
        require(pointState != EMPTY) { "Must be passed stone colour" }

        state.forEachIndexed { xCoord, columnArray ->
            columnArray.forEachIndexed { yCoord, point ->
                if (point == pointState) {
                    removeStone(xCoord, yCoord)
                }
            }
        }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is BoardPosition) return false

        if (xSize != other.xSize) return false
        if (ySize != other.ySize) return false
        if (!state.contentDeepEquals(other.state)) return false

        return true
    }

    override fun hashCode(): Int {
        var result = xSize
        result = 31 * result + ySize
        result = 31 * result + state.contentDeepHashCode()
        return result
    }

    override fun toString(): String {
        return "Board(xSize=$xSize, ySize=$ySize, state=${state.contentToString()})"
    }
}